import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faUsersSlash } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { ICustomer } from "redux/reducers/models/customer";
import { delay } from "helpers/Helpers";

import Icon from "components/icon/Icon";
import Spin from "components/spin/spin";
import { Badge } from "components/badge/Badge";
import { Select } from "components/select";

import "./creditBookSelectCustomer.scss";

const CUSTOMER_LIMIT = 50;

interface IProps {
  label: string;
  placeholder: string;
  customerIdsWithCreditBook: Set<number>;
  onCustomerChange: (customer: ICustomer | null) => void;
}

export default function CreditBookSelectCustomer(props: IProps) {
  const { label, placeholder, customerIdsWithCreditBook, onCustomerChange } = props;
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");
  const [customerList, setCustomerList] = useState<ICustomer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);

  useEffect(() => {
    const source = axios.CancelToken.source();

    const loadCustomers = async () => {
      if (searchString.length > 0 && searchString.length < 3) {
        setCustomerList([]);
        setIsLoadingCustomers(false);
        return;
      }

      setIsLoadingCustomers(true);

      await delay(300);

      const params: Parameters<typeof GetCustomer>[0] = { limit: CUSTOMER_LIMIT };
      if (searchString.length >= 3) {
        params.search = searchString;
      }

      const res = await GetCustomer(params, false, source.token);
      if (source.token.reason) {
        return;
      }
      if (res.status !== StatusCode.OK) {
        setCustomerList([]);
        setIsLoadingCustomers(false);
        return;
      }

      const customers = res.data ?? [];
      const uniqueCustomers = Array.from(new Map(customers.map(customer => [customer.id, customer])).values());
      setCustomerList(uniqueCustomers);
      setIsLoadingCustomers(false);
    };

    void loadCustomers();

    return () => source.cancel();
  }, [searchString]);

  function handleCustomerSelection(id: string | number) {
    if (id === null || id === undefined || id === "") {
      onCustomerChange(null);
      setSearchString("");
      return;
    }

    const customerId = Number(id);
    if (Number.isNaN(customerId) || customerIdsWithCreditBook.has(customerId)) {
      return;
    }

    const selectedCustomer = customerList?.find(customer => customer.id === customerId);
    if (selectedCustomer) {
      onCustomerChange(selectedCustomer);
      setSearchString(formatCustomerLabel(selectedCustomer));
    }
  }

  function formatCustomerLabel(customer: ICustomer): string {
    if (customer.email) {
      return `${customer.full_name} • ${customer.email}`;
    }
    return customer.full_name;
  }

  return (
    <div className="credit-book-select-customer-field">
    <Select
      showSearch
      showDropDownOnFocus
      allowClear
      smallDropdownSize
      label={label}
      leftIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
      className="credit-book-select-customer"
      onSearch={(query: string) => setSearchString(query)}
      onChange={(id: string | number) => handleCustomerSelection(id)}
      searchValue={searchString}
      searching={isLoadingCustomers}
      placeholder={placeholder}
      id="credit_book_select_customer_input"
      noData={
        <div className="credit-book-select-customer__no-data">
          <Icon icon={faUsersSlash} className="m-0" />
          <span>{t("No users found...")}</span>
        </div>
      }
    >
      {isLoadingCustomers ? (
        <Select.Option key="__loading" value="__loading" disabled name="">
          <div className="credit-book-select-customer__loading">
            <Spin />
            <span>{t("Loading customers...")}</span>
          </div>
        </Select.Option>
      ) : (
        customerList.map(customer => {
          const hasCreditBook = customerIdsWithCreditBook.has(customer.id);

          return (
            <Select.Option key={customer.id} value={customer.id} disabled={hasCreditBook} name={customer.full_name}>
              <div className="credit-book-select-customer__option">
                <span
                  className={
                    hasCreditBook
                      ? "credit-book-select-customer__option-label is-disabled"
                      : "credit-book-select-customer__option-label"
                  }
                >
                  {formatCustomerLabel(customer)}
                </span>
                {hasCreditBook ? (
                  <Badge type="gray" size="xsmall" className="credit-book-select-customer__exists-badge">
                    {t("Credit book exists")}
                  </Badge>
                ) : null}
              </div>
            </Select.Option>
          );
        })
      )}
    </Select>
    </div>
  );
}
